/**
 * お知らせ作成・編集ページ（シンプル版）
 * 公開ページ用のお知らせ管理
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminClient,
  TypeUtils,
} from "../../../api/generated/APIClient";
import { ImageInfo, ImageManager } from "../../../components/ui/ImageManager";
import { MarkdownEditor } from "../../../components/ui/MarkdownEditor";
import { getImageUrl } from "../../../utils/imageFetcher";
import "./AnnounceFormPage.css";

interface AnnounceMedia {
  name: string;
  size: number;
  contentType: string;
  mediaType: string;
  storagePath: string;
  displayOrder: number;
  caption: string;
  keyName?: string;
}

interface AnnounceFormData {
  id?: string;
  title: string;
  content: string;
  thumbnailImage?: ImageInfo | null;
  galleryImages: ImageInfo[];
  videos: ImageInfo[];
  contentMedia: ImageInfo[]; // 文章中に挿入するメディア
  displayPriority: number;
  publishDate: string;
  isPublished: boolean;
  createDate?: string;
  updateDate?: string;
  version?: string;
}

export const AnnounceFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<AnnounceFormData>({
    title: "",
    content: "",
    thumbnailImage: null,
    galleryImages: [],
    videos: [],
    contentMedia: [],
    displayPriority: 0,
    publishDate: new Date().toISOString().slice(0, 16),
    isPublished: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // R2から画像を削除する関数
  const deleteImageFromStorage = async (storagePath: string): Promise<void> => {
    if (!storagePath) return;
    try {
      await (AdminClient as any).DeleteAnnounceMedia({
        storagePath: storagePath,
      });
      console.log(`[AnnounceFormPage] ストレージから画像を削除しました: ${storagePath}`);
    } catch (error) {
      console.error(`[AnnounceFormPage] 画像削除に失敗: ${storagePath}`, error);
    }
  };

  // サムネイル画像の変更ハンドラ
  const handleThumbnailImageChange = async (images: ImageInfo[]) => {
    const newImage = images.length > 0 ? images[0] : null;
    const oldImage = formData.thumbnailImage;

    if (
      oldImage?.storagePath &&
      (!newImage || newImage.storagePath !== oldImage.storagePath)
    ) {
      await deleteImageFromStorage(oldImage.storagePath);
    }

    setFormData((prev) => ({
      ...prev,
      thumbnailImage: newImage,
    }));
  };

  // ギャラリー画像の変更ハンドラ
  const handleGalleryImagesChange = async (newImages: ImageInfo[]) => {
    const oldImages = formData.galleryImages;

    for (const oldImg of oldImages) {
      if (oldImg.storagePath) {
        const stillExists = newImages.some(
          (newImg) => newImg.storagePath === oldImg.storagePath
        );
        if (!stillExists) {
          await deleteImageFromStorage(oldImg.storagePath);
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      galleryImages: newImages,
    }));
  };

  // 動画の変更ハンドラ
  const handleVideosChange = async (newVideos: ImageInfo[]) => {
    const oldVideos = formData.videos;

    for (const oldVideo of oldVideos) {
      if (oldVideo.storagePath) {
        const stillExists = newVideos.some(
          (newVideo) => newVideo.storagePath === oldVideo.storagePath
        );
        if (!stillExists) {
          await deleteImageFromStorage(oldVideo.storagePath);
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      videos: newVideos,
    }));
  };

  // コンテンツメディアの変更ハンドラ（文章中に挿入する画像・動画）
  const handleContentMediaChange = async (newMedia: ImageInfo[]) => {
    const oldMedia = formData.contentMedia;

    for (const oldItem of oldMedia) {
      if (oldItem.storagePath) {
        const stillExists = newMedia.some(
          (newItem) => newItem.storagePath === oldItem.storagePath
        );
        if (!stillExists) {
          await deleteImageFromStorage(oldItem.storagePath);
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      contentMedia: newMedia,
    }));
  };

  // コンテンツに画像・動画を挿入
  const handleInsertContentMedia = (keyName: string) => {
    const media = formData.contentMedia.find((m) => m.keyName === keyName);
    if (!media) return;

    // メディアタイプに応じたMarkdown記法を生成
    const isVideo = media.mediaType === "video";
    const markdownSyntax = isVideo
      ? `![${media.name}](video:${keyName})`
      : `![${media.name}](image:${keyName})`;

    // 現在のコンテンツの末尾に追加（または適切な位置に挿入）
    setFormData((prev) => ({
      ...prev,
      content: prev.content + (prev.content.endsWith("\n") ? "" : "\n") + markdownSyntax + "\n",
    }));
  };

  // 編集モードの場合、データを取得
  useEffect(() => {
    if (isEditMode && id) {
      setIsLoading(true);
      const fetchAnnounce = async () => {
        try {
          const response = await (AdminClient as any).IndexAnnounce({
            offset: 0,
            limit: 0,
          });

          let items: any[] = [];
          if (response?.data) {
            if (Array.isArray(response.data)) {
              items = response.data;
            } else if (response.data.announces && Array.isArray(response.data.announces)) {
              items = response.data.announces;
            } else if (response.data.items && Array.isArray(response.data.items)) {
              items = response.data.items;
            }
          }

          const announce = items.find((item: any) => item.id === id);
          if (announce) {
            const generatePreviewUrl = (storagePath: string): string | undefined => {
              if (!storagePath) return undefined;
              return getImageUrl(storagePath) || undefined;
            };

            // サムネイル画像の変換
            const rawThumbnailImage = announce.thumbnailImage || announce.thumbnail_image;
            let thumbnailImage: ImageInfo | null = null;
            if (rawThumbnailImage) {
              const storagePath = rawThumbnailImage.storagePath || rawThumbnailImage.storage_path || "";
              thumbnailImage = {
                name: rawThumbnailImage.name || "",
                size: rawThumbnailImage.size || 0,
                sizeUnit: "B",
                keyName: "thumbnail",
                storagePath: storagePath,
                previewUrl: generatePreviewUrl(storagePath),
              };
            }

            // ギャラリー画像の変換
            const rawGalleryImages = announce.galleryImages || announce.gallery_images || [];
            const galleryImages: ImageInfo[] = rawGalleryImages.map((img: any, index: number) => {
              const storagePath = img.storagePath || img.storage_path || "";
              return {
                name: img.name || "",
                size: img.size || 0,
                sizeUnit: "B",
                keyName: `gallery-${index}`,
                storagePath: storagePath,
                previewUrl: generatePreviewUrl(storagePath),
              };
            });

            // 動画の変換
            const rawVideos = announce.videos || [];
            const videos: ImageInfo[] = rawVideos.map((video: any, index: number) => {
              const storagePath = video.storagePath || video.storage_path || "";
              return {
                name: video.name || "",
                size: video.size || 0,
                sizeUnit: "B",
                keyName: `video-${index}`,
                storagePath: storagePath,
                previewUrl: generatePreviewUrl(storagePath),
                mediaType: "video" as const,
              };
            });

            // コンテンツメディアの変換（文章中に挿入するメディア）
            const rawContentMedia = announce.contentMedia || announce.content_media || [];
            const contentMedia: ImageInfo[] = rawContentMedia.map((media: any) => {
              const storagePath = media.storagePath || media.storage_path || "";
              const mediaType = media.mediaType || media.media_type || "image";
              return {
                name: media.name || "",
                size: media.size || 0,
                sizeUnit: "B",
                keyName: media.keyName || media.key_name || "",
                storagePath: storagePath,
                previewUrl: generatePreviewUrl(storagePath),
                mediaType: mediaType as "image" | "video",
              };
            });

            // datetime-local用にJST固定でフォーマット
            const formatDateTimeLocalJST = (dateValue: any): string => {
              if (!dateValue) return new Date().toISOString().slice(0, 16);
              const date = new Date(dateValue);
              const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
              return jstDate.toISOString().slice(0, 16);
            };

            setFormData({
              id: announce.id,
              title: announce.title ?? "",
              content: announce.content ?? "",
              thumbnailImage: thumbnailImage,
              galleryImages: galleryImages,
              videos: videos,
              contentMedia: contentMedia,
              displayPriority: announce.displayPriority ?? announce.display_priority ?? 0,
              publishDate: formatDateTimeLocalJST(announce.publishDate || announce.publish_date),
              isPublished: announce.isPublished ?? announce.is_published ?? false,
              createDate: announce.createDate || announce.create_date,
              updateDate: announce.updateDate || announce.update_date,
              version: announce.version,
            });
          }
        } catch (error) {
          console.error("お知らせの取得に失敗しました:", error);
          alert("お知らせの取得に失敗しました");
        } finally {
          setIsLoading(false);
        }
      };

      fetchAnnounce();
    }
  }, [id, isEditMode]);

  const handleInputChange = (field: keyof AnnounceFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "タイトルを入力してください";
    }

    if (!formData.content.trim()) {
      newErrors.content = "内容を入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 画像をアップロードする関数
  const uploadMedia = async (
    imageInfo: ImageInfo,
    announceId: string,
    mediaType: "image" | "video"
  ): Promise<ImageInfo> => {
    if (imageInfo.storagePath) {
      return imageInfo;
    }

    if (!imageInfo.file) {
      throw new Error(`ファイルが見つかりません: ${imageInfo.name}`);
    }

    const arrayBuffer = await imageInfo.file.arrayBuffer();
    const fileData = new Uint8Array(arrayBuffer);

    const response = await (AdminClient as any).UploadAnnounceMedia({
      fileData: fileData,
      fileName: imageInfo.file.name,
      contentType: imageInfo.file.type,
      announceId: announceId,
      mediaType: mediaType,
      caption: "",
    });

    if (response.error || !response.data) {
      throw new Error(`アップロードに失敗しました: ${imageInfo.name}`);
    }

    // media オブジェクトから storagePath を取得
    const media = response.data.media || response.data;
    const storagePath = media.storagePath || media.storage_path || "";
    const previewUrl = getImageUrl(storagePath) || "";

    if (imageInfo.previewUrl && imageInfo.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(imageInfo.previewUrl);
    }

    return {
      ...imageInfo,
      storagePath: storagePath,
      previewUrl: previewUrl,
      file: undefined,
    };
  };

  // ImageInfo を AnnounceMedia に変換
  const toAnnounceMedia = (img: ImageInfo, mediaType: string, displayOrder: number, keyName?: string): AnnounceMedia => ({
    name: img.name,
    size: img.size || 0,
    contentType: img.file?.type || (mediaType === "video" ? "video/mp4" : "image/jpeg"),
    mediaType: mediaType,
    storagePath: img.storagePath || "",
    displayOrder: displayOrder,
    caption: "",
    keyName: keyName || img.keyName || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const announceId = formData.id || crypto.randomUUID();

      // サムネイル画像をアップロード
      let uploadedThumbnailImage = formData.thumbnailImage;
      if (uploadedThumbnailImage?.file) {
        uploadedThumbnailImage = await uploadMedia(
          { ...uploadedThumbnailImage, keyName: "thumbnail" },
          announceId,
          "image"
        );
      }

      // ギャラリー画像をアップロード
      const uploadedGalleryImages = await Promise.all(
        formData.galleryImages.map(async (img, index) => {
          if (img.file) {
            return await uploadMedia(
              { ...img, keyName: `gallery-${index}` },
              announceId,
              "image"
            );
          }
          return img;
        })
      );

      // 動画をアップロード
      const uploadedVideos = await Promise.all(
        formData.videos.map(async (video, index) => {
          if (video.file) {
            return await uploadMedia(
              { ...video, keyName: `video-${index}` },
              announceId,
              "video"
            );
          }
          return video;
        })
      );

      // コンテンツメディアをアップロード（文章中に挿入するメディア）
      const uploadedContentMedia = await Promise.all(
        formData.contentMedia.map(async (media) => {
          if (media.file) {
            const mediaType = media.mediaType || (media.file.type.startsWith("video/") ? "video" : "image");
            return await uploadMedia(
              media,
              announceId,
              mediaType as "image" | "video"
            );
          }
          return media;
        })
      );

      // datetime-local入力値をJSTとして解釈してUTCに変換
      const convertJSTToUTC = (datetimeLocalValue: string): string => {
        if (!datetimeLocalValue) return new Date().toISOString();
        const localDate = new Date(datetimeLocalValue);
        const utcDate = new Date(localDate.getTime() - 9 * 60 * 60 * 1000);
        return utcDate.toISOString();
      };

      const publishDate = formData.publishDate
        ? convertJSTToUTC(formData.publishDate)
        : new Date().toISOString();

      // 日付をDate型に変換するヘルパー
      const toDate = (date: any): Date => {
        if (!date) return new Date();
        if (date instanceof Date) return date;
        if (typeof date === "string") return new Date(date);
        if (date.seconds !== undefined) {
          const seconds = typeof date.seconds === "bigint"
            ? Number(date.seconds)
            : date.seconds;
          return new Date(seconds * 1000);
        }
        return new Date();
      };

      const submitData = {
        id: announceId,
        title: formData.title,
        content: formData.content,
        thumbnailImage: uploadedThumbnailImage
          ? toAnnounceMedia(uploadedThumbnailImage, "image", 0)
          : null,
        galleryImages: uploadedGalleryImages.map((img, index) =>
          toAnnounceMedia(img, "image", index)
        ),
        videos: uploadedVideos.map((video, index) =>
          toAnnounceMedia(video, "video", index)
        ),
        contentMedia: uploadedContentMedia.map((media, index) =>
          toAnnounceMedia(media, media.mediaType || "image", index, media.keyName)
        ),
        displayPriority: formData.displayPriority,
        publishDate: TypeUtils.dateToTimestamp(new Date(publishDate)),
        isPublished: formData.isPublished,
        createDate: TypeUtils.dateToTimestamp(
          isEditMode ? toDate(formData.createDate) : new Date()
        ),
        updateDate: TypeUtils.dateToTimestamp(new Date()),
        version: isEditMode ? formData.version : "v2",
      };

      if (isEditMode) {
        await (AdminClient as any).UpdateAnnounce({
          announces: [submitData],
        });
        alert("お知らせを更新しました");
        navigate(`/admin/entities/announce/${id}`);
      } else {
        await (AdminClient as any).CreateAnnounce({
          announces: [submitData],
        });
        alert("お知らせを作成しました");
        navigate("/admin/entities/announce");
      }
    } catch (error) {
      console.error("保存に失敗しました:", error);
      alert(
        `保存に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(`/admin/entities/announce/${id}`);
    } else {
      navigate("/admin/entities/announce");
    }
  };

  if (isLoading) {
    return (
      <div className="announce-form-page">
        <div className="loading">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="announce-form-page">
      <div className="announce-form-container">
        <div className="announce-form-header">
          <h1>{isEditMode ? "お知らせを編集" : "新しいお知らせを作成"}</h1>
          <p className="form-description">
            {isEditMode
              ? "お知らせの内容を編集できます"
              : "公開ページに表示されるお知らせを作成します"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="announce-form">
          {/* 基本情報セクション */}
          <section className="form-section">
            <h2 className="section-title">基本情報</h2>

            {/* タイトル */}
            <div className="form-field">
              <label htmlFor="title" className="form-label">
                タイトル <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className={`form-input ${errors.title ? "error" : ""}`}
                placeholder="お知らせのタイトルを入力してください"
              />
              {errors.title && (
                <div className="error-message">{errors.title}</div>
              )}
            </div>

            {/* 表示優先度 */}
            <div className="form-field">
              <label htmlFor="displayPriority" className="form-label">
                表示優先度
              </label>
              <input
                type="number"
                id="displayPriority"
                value={formData.displayPriority}
                onChange={(e) =>
                  handleInputChange("displayPriority", Number(e.target.value))
                }
                className="form-input"
                min={0}
              />
              <p className="field-help">
                数値が大きいほど上に表示されます（0が標準）
              </p>
            </div>
          </section>

          {/* メディア管理セクション */}
          <section className="form-section">
            <h2 className="section-title">メディア管理</h2>

            {/* サムネイル画像 */}
            <div className="form-field">
              <label className="form-label">サムネイル画像</label>
              <ImageManager
                images={formData.thumbnailImage ? [formData.thumbnailImage] : []}
                onImagesChange={handleThumbnailImageChange}
                onInsertImage={() => {}}
                title="サムネイル画像"
              />
              <p className="field-help">
                一覧ページで表示されるサムネイル画像です
              </p>
            </div>

            {/* ギャラリー画像 */}
            <div className="form-field">
              <label className="form-label">ギャラリー画像</label>
              <ImageManager
                images={formData.galleryImages}
                onImagesChange={handleGalleryImagesChange}
                onInsertImage={() => {}}
                title="ギャラリー画像"
              />
              <p className="field-help">
                詳細ページで表示される追加画像です（複数可）
              </p>
            </div>

            {/* 動画 */}
            <div className="form-field">
              <label className="form-label">動画</label>
              <ImageManager
                images={formData.videos}
                onImagesChange={handleVideosChange}
                onInsertImage={() => {}}
                title="動画"
                acceptTypes="video/*"
              />
              <p className="field-help">
                詳細ページで表示される動画です（複数可）
              </p>
            </div>
          </section>

          {/* 内容セクション */}
          <section className="form-section">
            <h2 className="section-title">内容</h2>

            {/* コンテンツメディア（文章中に挿入する画像・動画） */}
            <div className="form-field">
              <label className="form-label">コンテンツメディア</label>
              <ImageManager
                images={formData.contentMedia}
                onImagesChange={handleContentMediaChange}
                onInsertImage={handleInsertContentMedia}
                title="コンテンツ画像"
                acceptTypes="image/*,video/*"
              />
              <p className="field-help">
                文章中に挿入する画像・動画です。キー名を設定し、ツールバーから本文に挿入できます。
                <br />
                <code>![代替テキスト](image:キー名)</code> または <code>![代替テキスト](video:キー名)</code> 形式で参照されます。
              </p>
            </div>

            <div className="form-field">
              <label htmlFor="content" className="form-label">
                内容 <span className="required">*</span>
              </label>
              <MarkdownEditor
                value={formData.content}
                onChange={(newValue) => handleInputChange("content", newValue)}
                lineLength={80}
                placeholder="お知らせの内容をMarkdown形式で入力してください"
                availableImages={formData.contentMedia.map((media) => ({
                  keyName: media.keyName,
                  name: media.name,
                  previewUrl: media.previewUrl,
                  objectName: media.storagePath,
                }))}
                onInsertImage={(keyName) => handleInsertContentMedia(keyName)}
              />
              {errors.content && (
                <div className="error-message">{errors.content}</div>
              )}
              <p className="field-help">
                Markdown形式で入力できます。コンテンツメディアで追加した画像・動画は、ツールバーから挿入できます。
              </p>
            </div>
          </section>

          {/* 公開設定セクション */}
          <section className="form-section">
            <h2 className="section-title">公開設定</h2>

            {/* 公開日 */}
            <div className="form-field">
              <label htmlFor="publishDate" className="form-label">
                公開日
              </label>
              <input
                type="datetime-local"
                id="publishDate"
                value={formData.publishDate}
                onChange={(e) =>
                  handleInputChange("publishDate", e.target.value)
                }
                className="form-input"
              />
              <p className="field-help">
                この日時以降に公開ページで表示されます
              </p>
            </div>

            {/* 公開フラグ */}
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isPublished}
                  onChange={(e) =>
                    handleInputChange("isPublished", e.target.checked)
                  }
                />
                <span>公開する</span>
              </label>
              <p className="field-help">
                チェックを外すと、公開ページには表示されません
              </p>
            </div>
          </section>

          {/* ボタン */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "保存中..."
                : isEditMode
                ? "更新する"
                : "作成する"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
